import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const CurrencyContext = createContext(null);
const KEY = "atelier_currency";

// CHF is the default; a saved customer choice (CHF/EUR) is remembered.
const detectCurrency = (available) => {
  try {
    const saved = localStorage.getItem(KEY);
    if (saved && available.includes(saved)) return saved;
  } catch (e) {}
  return "chf";
};

// Must match backend convert_price: CHF exact; other currencies rounded UP to whole units.
const convertFromChf = (baseChf, currency, rate) => {
  if (currency === "chf") return Math.round(Number(baseChf) * 100) / 100;
  const cents = Math.round(Number(baseChf) * 100);
  const r4 = Math.round(rate * 10000);
  return Math.ceil((cents * r4) / 1000000);
};

const formatNumber = (n) =>
  n % 1 === 0
    ? n.toLocaleString("en-US")
    : n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export const CurrencyProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [currency, setCurrencyState] = useState("chf");
  const [ready, setReady] = useState(false);

  useEffect(() => {
    api
      .get("/config")
      .then((r) => {
        setConfig(r.data);
        const available = Object.keys(r.data.currencies || { chf: {} });
        setCurrencyState(detectCurrency(available));
      })
      .catch(() => setConfig({ currencies: { chf: { symbol: "CHF", rate: 1 } } }))
      .finally(() => setReady(true));
  }, []);

  const setCurrency = useCallback((c) => {
    setCurrencyState(c);
    try {
      localStorage.setItem(KEY, c);
    } catch (e) {}
  }, []);

  const meta = (config?.currencies || {})[currency] || { symbol: "CHF", rate: 1 };
  const rate = meta.rate ?? 1;
  const symbol = meta.symbol ?? "CHF";

  const convert = useCallback((baseChf) => convertFromChf(baseChf, currency, rate), [currency, rate]);
  const format = useCallback((baseChf) => `${symbol}${formatNumber(convert(baseChf))}`, [symbol, convert]);
  const formatValue = useCallback((value) => `${symbol}${formatNumber(Number(value))}`, [symbol]);
  // Switzerland-only shipping, defined in CHF and converted like prices.
  const shippingFor = useCallback((subtotal) => {
    const cfg = config?.shipping?.chf || {};
    const freeOver = cfg.free_over ? convert(cfg.free_over) : 0;
    return freeOver && subtotal >= freeOver ? 0 : convert(cfg.fee || 0);
  }, [config, convert]);

  const value = {
    ready,
    currency,
    symbol,
    rate,
    config,
    currencies: config?.currencies || {},
    shipping: config?.shipping || {},
    homepage: config?.homepage || {},
    setCurrency,
    convert,
    format,
    formatValue,
    shippingFor,
  };

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => useContext(CurrencyContext);
